import { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';
import { CollectionOperations } from '../components/CollectionOperations';
import { CollectionWorkstation } from '../components/CollectionWorkstation';

type MainTab = 'workstation' | 'list';

export function Collections() {
  const [mainTab, setMainTab] = useState<MainTab>('workstation');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<{
    installmentsMarkedOverdue: number;
    collectionsCreated: number;
    collectionsAlreadyExisting: number;
  } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getCollections(statusFilter ? { status: statusFilter } : {});
      setItems(data);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mainTab === 'list') load();
  }, [statusFilter, mainTab]);

  const runJob = async () => {
    setRunning(true);
    setError(null);
    try {
      setRunResult(await apiService.runCollectionsJob());
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setRunning(false);
    }
  };

  const totalPending = items.filter((i) => i.status === 'PENDING').reduce((s, i) => s + i.amount, 0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Cobranzas</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMainTab('workstation')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              mainTab === 'workstation' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Gestión por DNI
          </button>
          <button
            type="button"
            onClick={() => setMainTab('list')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              mainTab === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Lista de mora
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {mainTab === 'workstation' && <CollectionWorkstation />}

      {mainTab === 'list' && (
        <>
          <div className="flex gap-2 items-center mb-4 justify-end">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Todos los estados</option>
              <option value="PENDING">Pendientes</option>
              <option value="COLLECTED">Cobradas</option>
            </select>
            <button
              onClick={runJob}
              disabled={running}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
            >
              {running ? 'Corriendo…' : 'Correr job de mora'}
            </button>
          </div>

          {runResult && (
            <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-sm">
              Job ejecutado: <strong>{runResult.installmentsMarkedOverdue}</strong> cuotas marcadas OVERDUE,{' '}
              <strong>{runResult.collectionsCreated}</strong> cobranzas nuevas,{' '}
              <strong>{runResult.collectionsAlreadyExisting}</strong> ya existían.
              <button onClick={() => setRunResult(null)} className="ml-3 text-xs underline">
                Cerrar
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Cobranzas en lista</p>
              <p className="text-2xl font-bold text-gray-900">{items.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Total pendiente</p>
              <p className="text-2xl font-bold text-red-700">${totalPending.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Filtro activo</p>
              <p className="text-2xl font-bold text-gray-900">{statusFilter || 'Todos'}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-gray-500">Cargando…</div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center text-gray-500">No hay cobranzas. ¡Buena noticia!</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vence</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Préstamo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalle</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cobrada</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{c.dueDate?.slice(0, 10)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {c.customer?.firstName} {c.customer?.lastName}
                        <div className="text-xs text-gray-500">{c.customer?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                        {c.loanId?.slice(0, 8)}…
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{c.notes ?? '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        ${c.amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' +
                            (c.status === 'PENDING' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800')
                          }
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {c.collectedDate?.slice(0, 10) ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <CollectionOperations />
    </div>
  );
}
